import { Injectable } from '@angular/core';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export type TicketListViewMode = 'jours' | 'mois' | 'annees';

export type TicketTotalFilterOperator = '=' | '>' | '<' | 'between';

/** Filtres liste tickets / négoce partagés entre les onglets d’une même section. */
export interface TicketListFiltersSession {
  viewMode: TicketListViewMode;
  currentMonthIndex: number;
  currentYear: number;
  selectedMonthIndex: number | null;
  selectedYear: number | null;
  selectedDay: string;
  dateFilterStart: string;
  dateFilterEnd: string;
  searchTerm: string;
  selectedEstablishmentIds: string[];
  selectedWarehouseIds: string[];
  selectedPaymentTypes: string[];
  selectedTicketTypes: string[];
  selectedTicketStatuts: string[];
  totalFilterOperator: TicketTotalFilterOperator | '';
  totalFilterValue: number | null;
  totalFilterValueMax: number | null;
  basicSelectedOption: number;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseTicketFiltersService {
  constructor(private readonly authService: LogicielCaisseAuthService) {}

  load(context: 'tickets' | 'negoce'): TicketListFiltersSession | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(context));
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<TicketListFiltersSession>;
      // Un état « toutes les dates » (dateFilterStart/End vides) est valide :
      // on ne force plus le filtre date du jour par défaut.
      return {
        viewMode: parsed.viewMode === 'mois' || parsed.viewMode === 'annees' ? parsed.viewMode : 'jours',
        currentMonthIndex: Number.isFinite(Number(parsed.currentMonthIndex))
          ? Number(parsed.currentMonthIndex)
          : new Date().getMonth(),
        currentYear: Number.isFinite(Number(parsed.currentYear))
          ? Number(parsed.currentYear)
          : new Date().getFullYear(),
        selectedMonthIndex:
          parsed.selectedMonthIndex === null || parsed.selectedMonthIndex === undefined
            ? null
            : Number(parsed.selectedMonthIndex),
        selectedYear:
          parsed.selectedYear === null || parsed.selectedYear === undefined
            ? null
            : Number(parsed.selectedYear),
        selectedDay: String(parsed.selectedDay ?? ''),
        dateFilterStart: String(parsed.dateFilterStart ?? ''),
        dateFilterEnd: String(parsed.dateFilterEnd ?? ''),
        searchTerm: String(parsed.searchTerm ?? ''),
        selectedEstablishmentIds: Array.isArray(parsed.selectedEstablishmentIds)
          ? parsed.selectedEstablishmentIds.map((id) => String(id))
          : [],
        selectedWarehouseIds: Array.isArray(parsed.selectedWarehouseIds)
          ? parsed.selectedWarehouseIds.map((id) => String(id))
          : [],
        selectedPaymentTypes: Array.isArray(parsed.selectedPaymentTypes)
          ? parsed.selectedPaymentTypes.map((v) => String(v))
          : [],
        selectedTicketTypes: Array.isArray(parsed.selectedTicketTypes)
          ? parsed.selectedTicketTypes.map((v) => String(v))
          : [],
        selectedTicketStatuts: Array.isArray(parsed.selectedTicketStatuts)
          ? parsed.selectedTicketStatuts.map((v) => String(v))
          : [],
        totalFilterOperator: parseTotalFilterOperator(parsed.totalFilterOperator),
        totalFilterValue: parseOptionalNumber(parsed.totalFilterValue),
        totalFilterValueMax: parseOptionalNumber(parsed.totalFilterValueMax),
        basicSelectedOption: [10, 25, 50, 100].includes(Number(parsed.basicSelectedOption))
          ? Number(parsed.basicSelectedOption)
          : 10,
      };
    } catch {
      return null;
    }
  }

  save(context: 'tickets' | 'negoce', state: TicketListFiltersSession): void {
    try {
      sessionStorage.setItem(this.storageKey(context), JSON.stringify(state));
    } catch {
      /* quota / mode privé */
    }
  }

  clear(context: 'tickets' | 'negoce'): void {
    sessionStorage.removeItem(this.storageKey(context));
  }

  private storageKey(context: 'tickets' | 'negoce'): string {
    const station = this.authService.getSession()?.code_station ?? 'default';
    return `erp_caisse_list_filters_${station}_${context}`;
  }
}

function parseTotalFilterOperator(value: unknown): TicketTotalFilterOperator | '' {
  const op = String(value ?? '').trim();
  if (op === '=' || op === '>' || op === '<' || op === 'between') {
    return op;
  }
  return '';
}

function parseOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
